import { PDFJS_ASSETS, pdfjsLib } from "./pdf-library";
import {
  EventBus,
  LinkTarget,
  PDFFindController,
  PDFLinkService,
  PDFViewer,
} from "pdfjs-dist/web/pdf_viewer.mjs";
import type { PDFDocumentProxy } from "pdfjs-dist";
import {
  PDF_CLICK_TOLERANCE,
  PdfMeasurer,
  type PdfMeasureState,
  type PdfMeasureTool,
} from "./pdf-measure";
import { PdfComparer } from "./pdf-compare";
import { PdfSheetReader } from "./pdf-sheets";

// "auto" fits a landscape sheet to the page and a portrait one to the width,
// which suits drawing sets and specifications alike.
export type PdfScaleMode = "auto" | "page-width" | "page-fit";

// The markup tools are pdf.js's own annotation editors, so what is drawn is
// written into the PDF as standard annotations other viewers show too.
export type PdfMarkupTool = "PEN" | "HIGHLIGHT" | "TEXT" | "STAMP";

export interface PdfMarkupStyle {
  color: string;
  // pen thickness, or a text note's font size
  thickness: number;
  fontSize: number;
}

const markupEditorTypes: { [tool in PdfMarkupTool]: number } = {
  PEN: pdfjsLib.AnnotationEditorType.INK,
  HIGHLIGHT: pdfjsLib.AnnotationEditorType.HIGHLIGHT,
  TEXT: pdfjsLib.AnnotationEditorType.FREETEXT,
  STAMP: pdfjsLib.AnnotationEditorType.STAMP,
};

// the swatches the highlight editor's own colour picker offers
const HIGHLIGHT_COLORS =
  "yellow=#FFFF98,green=#53FFBC,blue=#80EBFF,pink=#FFCBE6,red=#FF4F5F";

// Dragging the document moves it, the way drawings are read on screen; select
// mode gives the mouse back to the text layer.
export type PdfPointerMode = "PAN" | "SELECT";

export interface PdfOutlineItem {
  title: string;
  dest: string | any[] | null;
  url: string | null;
  items: PdfOutlineItem[];
}

export interface PdfSearchMatches {
  current: number;
  total: number;
}

// Everything the toolbar and the rail panels show, pushed to React whenever
// pdf.js moves the page, the zoom or the search.
export interface PdfViewerState extends PdfMeasureState {
  pageNumber: number;
  pageCount: number;
  scale: number;
  rotation: number;
  searchMatches: PdfSearchMatches;
  searchNotFound: boolean;
  // markups drawn since the PDF opened that are not saved yet
  markupModified: boolean;
  // each page's sheet number, read from its title block
  sheetNumbers: { [pageNumber: number]: string };
  sheetsRead: boolean;
}

const THUMBNAIL_WIDTH = 132;

// Pages print as images at about the resolution of an office printer, with the
// long side capped so a large sheet does not exhaust the browser's memory.
const PRINT_DPI = 150;
const PRINT_MAX_SIDE = 4000;

/**
 * The PDF viewer: pdf.js's own page viewer, link service and find controller,
 * wired together the way Firefox's viewer wires them. Pages render lazily as
 * they scroll into view, and the text layer keeps text selectable and
 * searchable.
 */
export class PdfScene {
  private container: HTMLDivElement;
  private eventBus: EventBus;
  private linkService: PDFLinkService;
  private findController: PDFFindController;
  private viewer: PDFViewer;
  private pdfDocument: PDFDocumentProxy | null = null;
  private loadingTask: any = null;
  private onChange: (viewerState: Partial<PdfViewerState>) => void;
  private searchQuery = "";
  private resizeObserver: ResizeObserver;
  private pointerMode: PdfPointerMode = "PAN";
  private markupTool: PdfMarkupTool | null = null;
  // Until the first page's editor layer has drawn, the tool asked for waits:
  // turning editing on earlier trips over markups already in the PDF.
  private editorLayerReady = false;
  private pendingMarkupTool: PdfMarkupTool | null = null;
  measurer: PdfMeasurer;
  comparer: PdfComparer;
  private sheetReader: PdfSheetReader;
  // read once the first page shows, so it does not hold the first page up
  private sheetsPending: PDFDocumentProxy | null = null;
  private panStart: {
    x: number;
    y: number;
    scrollLeft: number;
    scrollTop: number;
  } | null = null;

  constructor(
    container: HTMLDivElement,
    compareContainer: HTMLDivElement,
    onChange: (viewerState: Partial<PdfViewerState>) => void,
  ) {
    this.container = container;
    this.onChange = onChange;

    this.eventBus = new EventBus();
    this.linkService = new PDFLinkService({
      eventBus: this.eventBus,
      // a web link in the PDF must not navigate the app away
      externalLinkTarget: LinkTarget.BLANK,
    });
    this.findController = new PDFFindController({
      eventBus: this.eventBus,
      linkService: this.linkService,
    });
    this.viewer = new PDFViewer({
      container,
      eventBus: this.eventBus,
      linkService: this.linkService,
      findController: this.findController,
      // NONE, not DISABLE: the editors are built, and wait for a markup tool
      annotationEditorMode: pdfjsLib.AnnotationEditorType.NONE,
      annotationEditorHighlightColors: HIGHLIGHT_COLORS,
    });
    this.linkService.setViewer(this.viewer);
    this.measurer = new PdfMeasurer(this.viewer, onChange);
    this.comparer = new PdfComparer(
      this.viewer,
      this.eventBus,
      container,
      compareContainer,
    );
    this.sheetReader = new PdfSheetReader((sheetNumbers, sheetsRead) => {
      onChange({ sheetNumbers, sheetsRead });
    });

    this.eventBus.on("pagesinit", this.HandlePagesInit);
    this.eventBus.on("pagerendered", this.HandlePageRendered);
    this.eventBus.on(
      "annotationeditorstateschanged",
      this.HandleEditorStatesChanged,
    );
    this.eventBus.on(
      "annotationeditorlayerrendered",
      this.HandleEditorLayerRendered,
    );
    this.eventBus.on("pagechanging", this.HandlePageChanging);
    this.eventBus.on("scalechanging", this.HandleScaleChanging);
    this.eventBus.on("rotationchanging", this.HandleRotationChanging);
    this.eventBus.on("updatefindmatchescount", this.HandleFindMatchesCount);
    this.eventBus.on("updatefindcontrolstate", this.HandleFindControlState);

    // Ctrl + wheel (and a trackpad pinch, which browsers report the same way)
    // zooms the document instead of the page.
    container.addEventListener("wheel", this.HandleWheel, { passive: false });
    container.addEventListener("mousedown", this.HandlePanStart);
    container.addEventListener("mousemove", this.HandleMeasureMove);
    // ahead of the viewer's own Esc, which would leave full screen
    window.addEventListener("keydown", this.HandleMeasureKeyDown, true);
    container.classList.add("pdfPanMode");

    // Going full screen, docking a panel or resizing the window changes the
    // room the pages have; a fitted zoom is fitted again to the new size.
    this.resizeObserver = new ResizeObserver(this.HandleResize);
    this.resizeObserver.observe(container);
  }

  private HandleResize = () => {
    if (!this.pdfDocument) return;

    const scaleValue = this.viewer.currentScaleValue;
    if (Number.isNaN(parseFloat(scaleValue))) {
      this.viewer.currentScaleValue = scaleValue;
    }
  };

  private HandlePagesInit = () => {
    this.viewer.currentScaleValue = "auto";
  };

  // pdf.js empties a page each time it draws it, measurements included
  private HandlePageRendered = ({ pageNumber }: { pageNumber: number }) => {
    this.measurer.DrawPage(pageNumber);

    if (this.sheetsPending) {
      this.sheetReader.Read(this.sheetsPending);
      this.sheetsPending = null;
    }
  };

  // Anything that can be undone is a change since the PDF opened. Markups
  // already in the PDF become editable without counting, and undoing every
  // change counts as none.
  private HandleEditorStatesChanged = ({
    details,
  }: {
    details: { hasSomethingToUndo: boolean };
  }) => {
    this.onChange({ markupModified: details.hasSomethingToUndo });
  };

  private HandleEditorLayerRendered = () => {
    if (this.editorLayerReady) return;

    this.editorLayerReady = true;
    this.SetMarkupTool(this.pendingMarkupTool);
  };

  private HandlePageChanging = ({ pageNumber }: { pageNumber: number }) => {
    this.onChange({ pageNumber });
  };

  private HandleScaleChanging = ({ scale }: { scale: number }) => {
    this.onChange({ scale });
  };

  private HandleRotationChanging = ({
    pagesRotation,
  }: {
    pagesRotation: number;
  }) => {
    this.onChange({ rotation: pagesRotation });
  };

  private HandleFindMatchesCount = ({
    matchesCount,
  }: {
    matchesCount: PdfSearchMatches;
  }) => {
    this.onChange({ searchMatches: matchesCount, searchNotFound: false });
  };

  private HandleFindControlState = ({
    state,
    matchesCount,
  }: {
    state: number;
    matchesCount: PdfSearchMatches;
  }) => {
    // FindState.NOT_FOUND
    this.onChange({
      searchMatches: matchesCount,
      searchNotFound: state === 1,
    });
  };

  private HandleWheel = (event: WheelEvent) => {
    if (!event.ctrlKey && !event.metaKey) return;

    event.preventDefault();
    if (event.deltaY < 0) {
      this.viewer.increaseScale();
    } else if (event.deltaY > 0) {
      this.viewer.decreaseScale();
    }
  };

  // While measuring, a click places a point and a drag still pans, whichever
  // pointer mode is set.
  private HandlePanStart = (event: MouseEvent) => {
    // a markup tool owns the mouse; its editors draw, move and resize
    if (this.markupTool) return;
    if (this.pointerMode !== "PAN" && !this.measurer.active) return;
    if (event.button !== 0) return;
    // links in the PDF still follow on a click, unless a point is being placed
    if (
      !this.measurer.active &&
      (event.target as HTMLElement).closest(".annotationLayer a")
    ) {
      return;
    }

    event.preventDefault();
    this.panStart = {
      x: event.clientX,
      y: event.clientY,
      scrollLeft: this.container.scrollLeft,
      scrollTop: this.container.scrollTop,
    };
    this.container.classList.add("pdfPanning");
    window.addEventListener("mousemove", this.HandlePanMove);
    window.addEventListener("mouseup", this.HandlePanEnd);
  };

  private HandlePanMove = (event: MouseEvent) => {
    if (!this.panStart) return;

    this.container.scrollLeft =
      this.panStart.scrollLeft - (event.clientX - this.panStart.x);
    this.container.scrollTop =
      this.panStart.scrollTop - (event.clientY - this.panStart.y);
  };

  private HandlePanEnd = (event?: MouseEvent) => {
    if (
      event &&
      this.panStart &&
      this.measurer.active &&
      Math.hypot(
        event.clientX - this.panStart.x,
        event.clientY - this.panStart.y,
      ) < PDF_CLICK_TOLERANCE
    ) {
      this.measurer.HandleClick(event);
    }
    this.panStart = null;
    this.container.classList.remove("pdfPanning");
    window.removeEventListener("mousemove", this.HandlePanMove);
    window.removeEventListener("mouseup", this.HandlePanEnd);
  };

  private HandleMeasureMove = (event: MouseEvent) => {
    if (!this.panStart) {
      this.measurer.HandleMove(event);
    }
  };

  private HandleMeasureKeyDown = (event: KeyboardEvent) => {
    // typing a known length must not undo the points it is for
    if ((event.target as HTMLElement).closest?.("input, textarea")) return;
    this.measurer.HandleKeyDown(event);
  };

  /**
   * Opens the PDF at a presigned Wasabi URL. Large files are read in ranges,
   * so the first pages show before the rest has arrived; if the browser or the
   * bucket refuses ranged requests the whole file is fetched instead.
   */
  async Open(
    url: string,
    onProgress: (percent: number | null) => void,
  ): Promise<number> {
    const documentOptions = {
      url,
      cMapUrl: `${PDFJS_ASSETS}/cmaps/`,
      cMapPacked: true,
      standardFontDataUrl: `${PDFJS_ASSETS}/standard_fonts/`,
    };

    let pdfDocument: PDFDocumentProxy;
    try {
      pdfDocument = await this.Load(documentOptions, onProgress);
    } catch (error: any) {
      // a password, or a file that is not a PDF at all, will not load either way
      if (
        error?.name === "PasswordException" ||
        error?.name === "InvalidPDFException"
      ) {
        throw error;
      }
      pdfDocument = await this.Load(
        { ...documentOptions, disableRange: true, disableStream: true },
        onProgress,
      );
    }

    // the old document's editing ends with it; the new one starts again
    // once its first editor layer has drawn
    if (this.markupTool) {
      this.viewer.annotationEditorMode = {
        mode: pdfjsLib.AnnotationEditorType.NONE,
      };
      this.pendingMarkupTool = this.markupTool;
      this.markupTool = null;
      this.container.classList.remove("pdfMarkupMode");
    }
    this.editorLayerReady = false;

    this.pdfDocument = pdfDocument;
    this.measurer.Reset();
    this.sheetReader.Cancel();
    this.sheetsPending = pdfDocument;
    this.onChange({
      markupModified: false,
      sheetNumbers: {},
      sheetsRead: false,
    });
    this.viewer.setDocument(pdfDocument);
    this.linkService.setDocument(pdfDocument, null);
    this.onChange({ pageNumber: 1, pageCount: pdfDocument.numPages });

    return pdfDocument.numPages;
  }

  private async Load(
    documentOptions: any,
    onProgress: (percent: number | null) => void,
  ): Promise<PDFDocumentProxy> {
    if (this.loadingTask) {
      await this.loadingTask.destroy();
    }

    this.loadingTask = pdfjsLib.getDocument(documentOptions);
    this.loadingTask.onProgress = ({
      loaded,
      total,
    }: {
      loaded: number;
      total: number;
    }) => {
      onProgress(
        total ? Math.min(99, Math.round((loaded / total) * 100)) : null,
      );
    };

    return this.loadingTask.promise;
  }

  Dispose() {
    this.container.removeEventListener("wheel", this.HandleWheel);
    this.container.removeEventListener("mousedown", this.HandlePanStart);
    this.container.removeEventListener("mousemove", this.HandleMeasureMove);
    window.removeEventListener("keydown", this.HandleMeasureKeyDown, true);
    this.HandlePanEnd();
    this.resizeObserver.disconnect();
    this.comparer.Dispose();
    this.sheetReader.Cancel();
    this.sheetsPending = null;
    this.viewer.setDocument(null as any);
    this.linkService.setDocument(null, null);
    if (this.loadingTask) {
      this.loadingTask.destroy();
      this.loadingTask = null;
    }
    this.pdfDocument = null;
  }

  // ---- pages

  GoToPage(pageNumber: number) {
    if (!this.pdfDocument) return;

    const page = Math.max(1, Math.min(this.pdfDocument.numPages, pageNumber));
    this.viewer.currentPageNumber = page;
  }

  NextPage() {
    this.viewer.nextPage();
  }

  PreviousPage() {
    this.viewer.previousPage();
  }

  // ---- zoom and rotation

  ZoomIn() {
    this.viewer.increaseScale();
  }

  ZoomOut() {
    this.viewer.decreaseScale();
  }

  SetScaleMode(scaleMode: PdfScaleMode) {
    this.viewer.currentScaleValue = scaleMode;
  }

  Rotate() {
    this.viewer.pagesRotation = (this.viewer.pagesRotation + 90) % 360;
  }

  // ---- pointer

  SetPointerMode(pointerMode: PdfPointerMode) {
    this.pointerMode = pointerMode;
    this.container.classList.toggle("pdfPanMode", pointerMode === "PAN");
  }

  // ---- measure

  SetMeasureTool(tool: PdfMeasureTool | null) {
    this.measurer.SetActiveTool(tool);
    this.container.classList.toggle("pdfMeasureMode", tool !== null);
  }

  // ---- markup

  /**
   * Hands the mouse to one of pdf.js's editors, or with null back to panning
   * or selecting. Markups already drawn stay on the sheet either way.
   */
  SetMarkupTool(tool: PdfMarkupTool | null) {
    this.pendingMarkupTool = tool;
    if (!this.pdfDocument || !this.editorLayerReady) return;
    if (tool === this.markupTool) return;

    this.CommitDrawing();
    this.markupTool = tool;
    this.container.classList.toggle("pdfMarkupMode", tool !== null);
    this.viewer.annotationEditorMode = {
      mode: tool ? markupEditorTypes[tool] : pdfjsLib.AnnotationEditorType.NONE,
    };
  }

  // A pen stroke stays open for more strokes until its layer loses focus to
  // another control, which a click on the panel does not do. It has to be
  // finished while its editor type is still the current one.
  private CommitDrawing() {
    for (let index = 0; index < this.viewer.pagesCount; index++) {
      (
        this.viewer.getPageView(index) as any
      )?.annotationEditorLayer?.annotationEditorLayer?.commitOrRemove();
    }
  }

  // A stamp is added from the panel rather than by clicking the sheet: the
  // new one lands on the page in view and asks for its picture, so this must
  // run straight from the click that asked for it.
  AddStamp() {
    this.eventBus.dispatch("switchannotationeditorparams", {
      source: this,
      type: pdfjsLib.AnnotationEditorParamsType.CREATE,
      value: null,
    });
  }

  // applies to the markups selected now and to the next ones drawn
  SetMarkupStyle({ color, thickness, fontSize }: PdfMarkupStyle) {
    const { AnnotationEditorParamsType } = pdfjsLib;
    for (const [type, value] of [
      [AnnotationEditorParamsType.INK_COLOR, color],
      [AnnotationEditorParamsType.INK_THICKNESS, thickness],
      [AnnotationEditorParamsType.FREETEXT_COLOR, color],
      [AnnotationEditorParamsType.FREETEXT_SIZE, fontSize],
    ] as [number, string | number][]) {
      this.eventBus.dispatch("switchannotationeditorparams", {
        source: this,
        type,
        value,
      });
    }
  }

  SetHighlightColor(color: string) {
    this.eventBus.dispatch("switchannotationeditorparams", {
      source: this,
      type: pdfjsLib.AnnotationEditorParamsType.HIGHLIGHT_DEFAULT_COLOR,
      value: color,
    });
  }

  // the PDF with its markups written in, as a new file
  async SaveWithMarkup(): Promise<Uint8Array | null> {
    if (!this.pdfDocument) return null;

    // a text note still being typed is committed first
    this.SetMarkupTool(null);
    return await this.pdfDocument.saveDocument();
  }

  // ---- download and print

  /**
   * The whole file as the viewer holds it. Read in ranges, pdf.js fetches
   * whatever has not arrived yet before handing it over.
   */
  async GetData(): Promise<Uint8Array | null> {
    if (!this.pdfDocument) return null;

    return await this.pdfDocument.getData();
  }

  /**
   * Draws every page as an image, one per printed sheet, the way the pages
   * are turned. Data URLs rather than blob URLs, which the site's CSP keeps
   * out of img-src.
   */
  async RenderPrintPages(
    onProgress: (pagesDone: number, pageCount: number) => void,
  ): Promise<string[]> {
    if (!this.pdfDocument) return [];

    const pdfDocument = this.pdfDocument;
    const pageImages: string[] = [];
    const canvas = document.createElement("canvas");

    for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber++) {
      onProgress(pageNumber - 1, pdfDocument.numPages);

      const page = await pdfDocument.getPage(pageNumber);
      const rotation = (page.rotate + this.viewer.pagesRotation) % 360;
      const unscaled = page.getViewport({ scale: 1, rotation });
      const scale = Math.min(
        PRINT_DPI / 72,
        PRINT_MAX_SIDE / Math.max(unscaled.width, unscaled.height),
      );
      const viewport = page.getViewport({ scale, rotation });

      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      const canvasContext = canvas.getContext("2d")!;
      // a page with no background of its own prints on white, not black
      canvasContext.fillStyle = "#ffffff";
      canvasContext.fillRect(0, 0, canvas.width, canvas.height);

      await page.render({ canvasContext, viewport, intent: "print" }).promise;
      pageImages.push(canvas.toDataURL("image/png"));
      page.cleanup();
    }

    canvas.width = 0;
    canvas.height = 0;
    onProgress(pdfDocument.numPages, pdfDocument.numPages);

    return pageImages;
  }

  // ---- thumbnails

  /**
   * Draws one page small into the canvas given, turned the way the pages are.
   * The pages panel calls this only for thumbnails scrolled into view.
   */
  async RenderThumbnail(pageNumber: number, canvas: HTMLCanvasElement) {
    if (!this.pdfDocument) return;

    const page = await this.pdfDocument.getPage(pageNumber);
    const rotation = (page.rotate + this.viewer.pagesRotation) % 360;
    const unscaled = page.getViewport({ scale: 1, rotation });
    const outputScale = window.devicePixelRatio || 1;
    const viewport = page.getViewport({
      scale: (THUMBNAIL_WIDTH / unscaled.width) * outputScale,
      rotation,
    });

    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    canvas.style.width = `${THUMBNAIL_WIDTH}px`;
    canvas.style.height = `${Math.floor(viewport.height / outputScale)}px`;

    await page.render({
      canvasContext: canvas.getContext("2d")!,
      viewport,
    }).promise;
  }

  // ---- outline

  async GetOutline(): Promise<PdfOutlineItem[]> {
    if (!this.pdfDocument) return [];

    return ((await this.pdfDocument.getOutline()) ?? []) as PdfOutlineItem[];
  }

  GoToOutlineItem(item: PdfOutlineItem) {
    if (item.dest) {
      this.linkService.goToDestination(item.dest);
    } else if (item.url) {
      window.open(item.url, "_blank", "noopener");
    }
  }

  // ---- search

  /**
   * Highlights every match of the query and moves to the next (or previous)
   * one. Repeating the same query steps through the matches.
   */
  Search(query: string, findPrevious = false) {
    const again = query === this.searchQuery;
    this.searchQuery = query;

    this.eventBus.dispatch("find", {
      source: this,
      type: again ? "again" : "",
      query,
      caseSensitive: false,
      entireWord: false,
      highlightAll: true,
      findPrevious,
      matchDiacritics: false,
    });
  }

  ClearSearch() {
    this.searchQuery = "";
    this.eventBus.dispatch("findbarclose", { source: this });
    this.onChange({
      searchMatches: { current: 0, total: 0 },
      searchNotFound: false,
    });
  }
}
